import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { MultiAccountService } from '../services/multiAccountService';
import { GoogleUser, TradingAccount } from '../types';
import { useNotification } from '../context/NotificationContext';
import PageHeader from './common/PageHeader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountManagement: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [googleUsers, setGoogleUsers] = useState<GoogleUser[]>([]);
  const [tradingAccounts, setTradingAccounts] = useState<TradingAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<TradingAccount | null>(null);
  const [editingUser, setEditingUser] = useState<GoogleUser | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<{ id: string; name: string } | null>(null);

  // Form states
  const [accountForm, setAccountForm] = useState({
    name: '',
    description: '',
    initialBalance: 1000000,  // 初始餘額
    balance: 1000000,         // 當前餘額（新帳戶時等於初始餘額）
    currency: 'TWD',
    accountType: 'personal' as 'personal' | 'business' | 'demo',
    isActive: true,
  });

  const [userForm, setUserForm] = useState({
    isAllowed: false,
    allowedAccounts: [] as string[],
    role: 'user' as 'admin' | 'user' | 'viewer',
    defaultAccountId: '',
  });

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const [users, accounts] = await Promise.all([
        MultiAccountService.getAllGoogleUsers(),
        MultiAccountService.getAllTradingAccounts(),
      ]);
      
      setGoogleUsers(users);
      setTradingAccounts(accounts);
    } catch (err) {
      showError('載入資料失敗');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    try {
      await MultiAccountService.createTradingAccount(accountForm);
      setOpenAccountDialog(false);
      resetAccountForm();
      loadData();
      showSuccess(`成功建立交易帳戶：${accountForm.name}`);
    } catch (err) {
      showError('建立交易帳戶失敗');
    }
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;
    
    try {
      await MultiAccountService.updateTradingAccount(editingAccount.id, accountForm);
      setOpenAccountDialog(false);
      setEditingAccount(null);
      resetAccountForm();
      loadData();
      showSuccess(`成功更新交易帳戶：${accountForm.name}`);
    } catch (err) {
      showError('更新交易帳戶失敗');
    }
  };

  const handleDeleteAccount = (accountId: string) => {
    const account = tradingAccounts.find(acc => acc.id === accountId);
    const accountName = account ? account.name : '此交易帳戶';
    
    setDeletingAccount({ id: accountId, name: accountName });
    setOpenDeleteDialog(true);
  };

  const confirmDeleteAccount = async () => {
    if (!deletingAccount) return;
    
    try {
      await MultiAccountService.deleteTradingAccount(deletingAccount.id);
      setOpenDeleteDialog(false);
      setDeletingAccount(null);
      loadData();
      showSuccess(`成功刪除交易帳戶：${deletingAccount.name}`);
    } catch (err) {
      showError('刪除交易帳戶失敗');
    }
  };

  const handleUpdateUserPermissions = async () => {
    if (!editingUser) return;
    
    try {
      await MultiAccountService.updateGoogleUserPermissions(
        editingUser.uid,
        userForm.isAllowed,
        userForm.allowedAccounts,
        userForm.role
      );
      
      if (userForm.defaultAccountId) {
        await MultiAccountService.setDefaultAccount(editingUser.uid, userForm.defaultAccountId);
      }
      
      setOpenUserDialog(false);
      setEditingUser(null);
      resetUserForm();
      loadData();
      showSuccess(`成功更新用戶權限：${editingUser.displayName}`);
    } catch (err) {
      showError('更新用戶權限失敗');
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      name: '',
      description: '',
      initialBalance: 1000000,
      balance: 1000000,
      currency: 'TWD',
      accountType: 'personal',
      isActive: true,
    });
  };

  const resetUserForm = () => {
    setUserForm({      isAllowed: false,
      allowedAccounts: [],
      role: 'user',
      defaultAccountId: '',
    });
  };

  const openEditAccount = (account: TradingAccount) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name,
      description: account.description || '',
      initialBalance: account.initialBalance || account.balance, // 向下相容：如果沒有 initialBalance 就使用 balance
      balance: account.balance,
      currency: account.currency,
      accountType: account.accountType,
      isActive: account.isActive,
    });
    setOpenAccountDialog(true);
  };

  const openEditUser = (user: GoogleUser) => {
    setEditingUser(user);
    setUserForm({
      isAllowed: user.isAllowed,
      allowedAccounts: user.allowedAccounts,
      role: user.role,
      defaultAccountId: user.defaultAccountId || '',
    });
    setOpenUserDialog(true);
  };

  if (loading) {
    return <Box sx={{ p: 3, textAlign: 'center' }}>載入中...</Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="帳戶管理系統"
        icon={SettingsIcon}
        subtitle={`管理交易帳戶和用戶權限 • ${tradingAccounts.length}個帳戶，${googleUsers.length}個用戶`}
        actions={null}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceIcon />
                交易帳戶管理
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon />
                用戶權限管理
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* 交易帳戶管理 */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">交易帳戶列表</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetAccountForm();
              setEditingAccount(null);
              setOpenAccountDialog(true);
            }}
          >
            新增帳戶
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>帳戶名稱</TableCell>
                <TableCell>描述</TableCell>
                <TableCell>餘額</TableCell>
                <TableCell>幣別</TableCell>
                <TableCell>類型</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tradingAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{account.name}</TableCell>
                  <TableCell>{account.description || '-'}</TableCell>
                  <TableCell>{account.balance.toLocaleString()}</TableCell>
                  <TableCell>{account.currency}</TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        account.accountType === 'personal' ? '個人' :
                        account.accountType === 'business' ? '企業' : '模擬'
                      }
                      color={
                        account.accountType === 'personal' ? 'primary' :
                        account.accountType === 'business' ? 'secondary' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={account.isActive ? '啟用' : '停用'}
                      color={account.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="編輯">
                      <IconButton size="small" onClick={() => openEditAccount(account)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="刪除">
                      <IconButton size="small" onClick={() => handleDeleteAccount(account.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* 用戶權限管理 */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" sx={{ mb: 3 }}>Google 用戶權限管理</Typography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>用戶</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>角色</TableCell>
                <TableCell>可用帳戶</TableCell>
                <TableCell>預設帳戶</TableCell>
                <TableCell>最後登入</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {googleUsers.map((user) => (
                <TableRow key={user.uid}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.displayName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.isAllowed ? '允許登入' : '禁止登入'}
                      color={user.isAllowed ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        user.role === 'admin' ? '管理員' :
                        user.role === 'user' ? '用戶' : '檢視者'
                      }
                      color={
                        user.role === 'admin' ? 'error' :
                        user.role === 'user' ? 'primary' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.allowedAccounts.map(accountId => {
                        const account = tradingAccounts.find(a => a.id === accountId);
                        return account ? (
                          <Chip key={accountId} label={account.name} size="small" variant="outlined" />
                        ) : null;
                      })}
                      {user.allowedAccounts.length === 0 && <Typography variant="body2" color="text.secondary">無</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.defaultAccountId ? 
                      tradingAccounts.find(a => a.id === user.defaultAccountId)?.name || '-' : '-'
                    }
                  </TableCell>
                  <TableCell>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString('zh-TW') : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="編輯權限">
                      <IconButton size="small" onClick={() => openEditUser(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* 新增/編輯帳戶 Dialog */}
      <Dialog open={openAccountDialog} onClose={() => setOpenAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccount ? '編輯交易帳戶' : '新增交易帳戶'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="帳戶名稱"
              value={accountForm.name}
              onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="描述"
              value={accountForm.description}
              onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="初始餘額"
              type="number"
              value={accountForm.initialBalance}
              onChange={(e) => {
                const newInitialBalance = Number(e.target.value);
                setAccountForm({ 
                  ...accountForm, 
                  initialBalance: newInitialBalance,
                  // 新帳戶時，當前餘額等於初始餘額
                  balance: editingAccount ? accountForm.balance : newInitialBalance
                });
              }}
              required
              fullWidth
              helperText={editingAccount ? "修改初始餘額只會影響餘額計算基準，不會直接改變當前餘額" : "新帳戶的當前餘額將等於初始餘額"}
            />
            <FormControl fullWidth>
              <InputLabel>幣別</InputLabel>
              <Select
                value={accountForm.currency}
                onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value })}
                label="幣別"
              >
                <MenuItem value="TWD">TWD (新台幣)</MenuItem>
                <MenuItem value="USD">USD (美元)</MenuItem>
                <MenuItem value="HKD">HKD (港幣)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>帳戶類型</InputLabel>
              <Select
                value={accountForm.accountType}
                onChange={(e) => setAccountForm({ ...accountForm, accountType: e.target.value as any })}
                label="帳戶類型"
              >
                <MenuItem value="personal">個人帳戶</MenuItem>
                <MenuItem value="business">企業帳戶</MenuItem>
                <MenuItem value="demo">模擬帳戶</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={accountForm.isActive}
                  onChange={(e) => setAccountForm({ ...accountForm, isActive: e.target.checked })}
                />
              }
              label="啟用帳戶"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAccountDialog(false)}>取消</Button>
          <Button 
            onClick={editingAccount ? handleUpdateAccount : handleCreateAccount}
            variant="contained"
          >
            {editingAccount ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編輯用戶權限 Dialog */}
      <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          編輯用戶權限 - {editingUser?.displayName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={userForm.isAllowed}
                  onChange={(e) => setUserForm({ ...userForm, isAllowed: e.target.checked })}
                />
              }
              label="允許登入系統"
            />
            <FormControl fullWidth>
              <InputLabel>用戶角色</InputLabel>
              <Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value as any })}
                label="用戶角色"
              >
                <MenuItem value="viewer">檢視者 - 只能查看</MenuItem>
                <MenuItem value="user">一般用戶 - 可以交易</MenuItem>
                <MenuItem value="admin">管理員 - 完整權限</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>可存取帳戶</InputLabel>
              <Select
                multiple
                value={userForm.allowedAccounts}
                onChange={(e) => setUserForm({ 
                  ...userForm, 
                  allowedAccounts: typeof e.target.value === 'string' ? [e.target.value] : e.target.value 
                })}
                label="可存取帳戶"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((accountId) => {
                      const account = tradingAccounts.find(a => a.id === accountId);
                      return account ? (
                        <Chip key={accountId} label={account.name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {tradingAccounts.filter(a => a.isActive).map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} ({account.accountType})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {userForm.allowedAccounts.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>預設帳戶</InputLabel>
                <Select
                  value={userForm.defaultAccountId}
                  onChange={(e) => setUserForm({ ...userForm, defaultAccountId: e.target.value })}
                  label="預設帳戶"
                >
                  <MenuItem value="">無</MenuItem>
                  {userForm.allowedAccounts.map((accountId) => {
                    const account = tradingAccounts.find(a => a.id === accountId);
                    return account ? (
                      <MenuItem key={accountId} value={accountId}>
                        {account.name}
                      </MenuItem>
                    ) : null;
                  })}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUserDialog(false)}>取消</Button>
          <Button onClick={handleUpdateUserPermissions} variant="contained">
            更新權限
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認 Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ 警告：即將永久刪除交易帳戶
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              「{deletingAccount?.name}」
            </Typography>
            <Typography variant="body1" paragraph>
              此操作將會永久刪除以下資料：
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" variant="body2">交易帳戶資料</Typography>
              <Typography component="li" variant="body2">所有交易記錄</Typography>
              <Typography component="li" variant="body2">投資組合資料</Typography>
              <Typography component="li" variant="body2">關注清單資料</Typography>
              <Typography component="li" variant="body2">用戶權限設定</Typography>
            </Box>
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ 此操作無法復原！
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenDeleteDialog(false);
              setDeletingAccount(null);
            }}
          >
            取消
          </Button>
          <Button 
            onClick={confirmDeleteAccount}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            確定刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountManagement;
